<script>
  export let values = null;
  export let total = null;
  export let previousValues = null;
  export let previousTotal = null;
  export let maxPercent = 100;
  export let showComparison = true;
  export let size = "base";
  export let tooltipContent = null;
  export let onTooltipMouseOver = null;
  export let onTooltipMouseOut = null;
  export let onTooltipMouseMove = null;

  const capacities = [1, 2, 3, 4, 5];
  const allowedSizes = ["sm", "base", "lg"];

  function normalizeNumber(value) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  function getPercent(sourceValues, sourceTotal, capacity) {
    if (!sourceValues) return 0;

    const rawValue = normalizeNumber(sourceValues[capacity]);
    const normalizedTotal = normalizeNumber(sourceTotal);

    if (normalizedTotal > 0) {
      return Math.round((rawValue / normalizedTotal) * 100);
    }

    return Math.max(0, Math.min(100, Math.round(rawValue)));
  }

  function getScaledWidth(percent, maxValue) {
    const normalizedMaxPercent = normalizeNumber(maxValue) || 100;
    const normalizedPercent = normalizeNumber(percent);

    return Math.max(0, Math.min(100, (normalizedPercent / normalizedMaxPercent) * 100));
  }

  function formatPercent(value) {
    return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
  }

  $: resolvedSize = allowedSizes.includes(size) ? size : "base";
  $: rawItems = capacities.map((capacity) => {
    const currentPercent = getPercent(values, total, capacity);
    const comparisonPercent = previousValues
      ? getPercent(previousValues, previousTotal, capacity)
      : currentPercent;

    return {
      capacity,
      currentPercent,
      comparisonPercent
    };
  });
  $: highestPercent = rawItems.reduce((maxValue, item) => {
    const comparisonMax = showComparison && previousValues ? item.comparisonPercent : 0;
    return Math.max(maxValue, item.currentPercent, comparisonMax);
  }, 0);
  $: resolvedMaxPercent = Math.max(normalizeNumber(maxPercent) || 100, highestPercent);
  $: axisTicks = [0, 1, 2, 3, 4].map((index) => (resolvedMaxPercent / 4) * index);
  $: items = rawItems.map((item) => ({
    ...item,
    currentWidth: getScaledWidth(item.currentPercent, resolvedMaxPercent),
    comparisonWidth: getScaledWidth(item.comparisonPercent, resolvedMaxPercent)
  }));
</script>

<div class="capacity-distribution-comparison" data-size={resolvedSize}>
  <div class="capacity-distribution-comparison__chart">
    <div class="capacity-distribution-comparison__grid">
      {#each axisTicks as tick}
        <div class="capacity-distribution-comparison__tick">
          <div class="capacity-distribution-comparison__tick-line"></div>
          <p class="capacity-distribution-comparison__tick-label">{formatPercent(tick)}</p>
        </div>
      {/each}
    </div>

    <div class="capacity-distribution-comparison__bars">
      {#each items as item}
        <button
          type="button"
          class="capacity-distribution-comparison__item"
          data-capacity={item.capacity}
          data-tooltip={tooltipContent ? tooltipContent(item.capacity) : undefined}
          aria-label={`Capacity ${item.capacity}: current ${item.currentPercent}%`}
          on:mouseover={onTooltipMouseOver}
          on:mouseout={onTooltipMouseOut}
          on:focus={onTooltipMouseOver}
          on:blur={onTooltipMouseOut}
          on:mousemove={onTooltipMouseMove}
        >
          <span
            class="capacity-distribution-comparison__bar-current"
            style={`width: ${item.currentWidth}%;`}
          ></span>

          {#if showComparison && previousValues && item.comparisonWidth > 0}
            <span
              class="capacity-distribution-comparison__bar-previous"
              style={`width: ${item.comparisonWidth}%;`}
            >
              <span class="capacity-distribution-comparison__bar-previous-dot"></span>
            </span>
          {/if}
        </button>
      {/each}
    </div>
  </div>
</div>
